/**
 * El precio que enseña la tarjeta de anuncio, en un solo sitio: la tarjeta lo pinta y el filtro
 * "Precio total" de la búsqueda filtra por él (× horas). Si cada uno lo calculara por su
 * cuenta, el filtro dejaría fuera anuncios cuyo total visible sí está en el rango.
 *
 * Todo en subunidades. El listing tiene que llegar con la cara ya aplicada (applyFaceToListing):
 * el precio por tramo sale de su `priceRange`.
 */
import { getCustomerFacingPriceSubunitsAndCurrency } from './cocopoolPricing';
import { convertUnitToSubUnit, unitDivisor } from './currency';
import { parseDateFromISO8601 } from './dates';
import { isFirstBookingDiscountEligible } from './firstBookingDiscount';
import { getWeekdayBookingDiscountPercent } from './weekdayBookingDiscount';
import { isBookingProcessAlias } from '../transactions/transaction';

const SEARCH_TZ = 'Europe/Madrid';

// Los descuentos se redondean a euros enteros, como siempre los ha enseñado la tarjeta.
const toWholeUnits = (amountSubunits, currency, discount) => {
  const divisor = unitDivisor(currency);
  const major = amountSubunits / divisor;
  if (!Number.isFinite(major) || major <= 0) {
    return null;
  }
  const discountedMajor = Math.max(0, Math.round(discount(major)));
  return discountedMajor > 0 ? convertUnitToSubUnit(String(discountedMajor), divisor) : null;
};

/** 20 % de primera reserva. */
export const firstBookingDiscountedSubunits = (amountSubunits, currency) =>
  toWholeUnits(amountSubunits, currency, major => major * 0.8);

/** Descuento entre semana del anfitrión (`percent`). */
export const weekdayDiscountedSubunits = (amountSubunits, currency, percent) =>
  percent ? toWholeUnits(amountSubunits, currency, major => (major * (100 - percent)) / 100) : null;

/**
 * Precio por hora de la tarjeta.
 *
 * @param {Object} params
 * @param {Object} params.listing con la cara aplicada
 * @param {Object} params.author el autor (perfil), para la elegibilidad de primera reserva
 * @param {String} params.capacity tramo de grupo buscado (p. ej. "11-15"), o null
 * @param {Boolean} params.isWeekday si la búsqueda es de un día L–V
 * @returns {{ amount, discountedAmount, currency } | null} `discountedAmount` es null sin
 *   descuento; primera reserva tiene preferencia sobre entre semana, como en la tarjeta.
 */
export const cardHourlyPrice = ({ listing, author, capacity, isWeekday }) => {
  const attrs = listing?.attributes;
  const customerFacing = getCustomerFacingPriceSubunitsAndCurrency(listing, capacity);
  const base = customerFacing || (attrs?.price ? attrs.price : null);
  if (!base || base.amount == null || !base.currency) {
    return null;
  }
  const amount = Number(base.amount);
  const { currency } = base;
  const publicData = attrs?.publicData;

  let discountedAmount = null;
  if (isBookingProcessAlias(publicData?.transactionProcessAlias)) {
    if (isFirstBookingDiscountEligible(publicData, author)) {
      discountedAmount = firstBookingDiscountedSubunits(amount, currency);
    } else if (isWeekday) {
      discountedAmount = weekdayDiscountedSubunits(
        amount,
        currency,
        getWeekdayBookingDiscountPercent(publicData)
      );
    }
  }
  return { amount, discountedAmount, currency };
};

/** Total de la reserva buscada: lo que se paga por hora (con descuento si hay) × horas. */
export const cardTotalSubunits = (hourlyPrice, hours) =>
  hourlyPrice && hours > 0
    ? Math.round((hourlyPrice.discountedAmount ?? hourlyPrice.amount) * hours)
    : null;

/**
 * Horas buscadas a partir de los params `dates` ("YYYY-MM-DD,YYYY-MM-DD") y `time`
 * ("HH:mm,HH:mm"), en hora de Madrid. `null` si falta alguno.
 */
export const searchDurationHours = (datesParam, timesParam) => {
  const values = typeof datesParam === 'string' ? datesParam.split(',') : [];
  const timeValues = typeof timesParam === 'string' ? timesParam.split(',') : [];
  if (values.length !== 2 || timeValues.length !== 2) {
    return null;
  }
  const startDate = parseDateFromISO8601(values[0], timeValues[0], SEARCH_TZ);
  const endDate = parseDateFromISO8601(values[1], timeValues[1], SEARCH_TZ);
  if (!startDate || !endDate) {
    return null;
  }
  return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
};
